import dayjs from "dayjs";
import type { CompanyProduct } from "../hooks/useCompanyProducts";

const CORE_HEADERS = [
  "name",
  "ean",
  "hs_code",
  "pieces_per_carton",
  "weight_per_carton",
  "carton_length",
  "carton_width",
  "carton_height",
  "unit_price",
] as const;

type CoreHeader = (typeof CORE_HEADERS)[number];

function escapeCsvCell(value: string): string {
  const needsQuotes =
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r");
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

function formatCellValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function productToRow(product: CompanyProduct): string[] {
  const row: Record<CoreHeader, string> = {
    name: formatCellValue(product.name),
    ean: formatCellValue(product.ean),
    hs_code: formatCellValue(product.hs_code),
    pieces_per_carton: formatCellValue(product.pieces_per_carton),
    weight_per_carton: formatCellValue(product.weight_per_carton),
    carton_length: formatCellValue(product.carton_length),
    carton_width: formatCellValue(product.carton_width),
    carton_height: formatCellValue(product.carton_height),
    unit_price: formatCellValue(product.unit_price),
  };

  return CORE_HEADERS.map((header) => row[header]);
}

export function buildCompanyProductsCsv(products: CompanyProduct[]): string {
  const lines = [
    CORE_HEADERS.join(","),
    ...products.map((product) =>
      productToRow(product).map((cell) => escapeCsvCell(cell)).join(","),
    ),
  ];

  return lines.join("\n");
}

export function getCompanyProductsExportFilename(): string {
  return `company-products-${dayjs().format("YYYY-MM-DD")}.csv`;
}

export function downloadCompanyProductsCsv(
  products: CompanyProduct[],
  filename = getCompanyProductsExportFilename(),
): void {
  const csvContent = buildCompanyProductsCsv(products);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}
